import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Btn, InputField, Field } from './UIComponents';
import api from '../../services/api';

export function AssessmentModal({ onClose, assessment, isEdit, onSave }) {
  const { B } = useTheme();
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    resident_id: assessment?.resident_id || '',
    type: assessment?.type || '',
    title: assessment?.title || '',
    due_date: assessment?.due_date || '',
    notes: assessment?.notes || ''
  });

  useEffect(() => {
    const fetchResidents = async () => {
      try {
        const res = await api.get('/residents', { params: { limit: 100, status: 'active' } });
        setResidents(res.data.data);
      } catch (err) {
        console.error('Failed to fetch residents:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchResidents();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!formData.resident_id || !formData.type || !formData.title || !formData.due_date) {
      alert('Please fill in all required fields.');
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit && assessment?.id) {
        await api.put(`/assessments/${assessment.id}`, formData);
      } else {
        await api.post('/assessments', formData);
      }
      if (onSave) onSave();
      onClose();
    } catch (err) {
      console.error('Failed to save assessment:', err);
      alert(err.response?.data?.message || 'Failed to save assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const assessmentTypes = ['intake', 'quarterly', 'annual', 'incident', 'discharge'];
  const typeLabels = {
    intake: 'Intake Assessment',
    quarterly: 'Quarterly Review',
    annual: 'Annual Assessment',
    incident: 'Incident Assessment',
    discharge: 'Discharge Assessment'
  };

  const modalStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  };

  const modalContentStyle = {
    background: B.cardBg,
    borderRadius: 16,
    width: '100%',
    maxWidth: 500,
    padding: 28,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    '@media (maxWidth: 768px)': {
      width: '90%',
      padding: 16,
    },
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 8,
    border: `1.5px solid ${B.border}`,
    fontSize: 13,
    background: B.inputBg,
    color: B.text,
    outline: 'none',
    fontFamily: 'inherit',
  };

  const buttonContainerStyle = {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    '@media (maxWidth: 768px)': {
      flexDirection: 'column',
      gap: 8,
    },
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 800, color: B.navy, marginBottom: 20 }}>
          {isEdit ? 'Edit Assessment' : 'New Assessment'}
        </div>

        {/* Resident Selection */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
            Resident <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <select name="resident_id" value={formData.resident_id} onChange={handleChange} style={inputStyle} disabled={loading}>
            <option value="">{loading ? 'Loading residents...' : 'Select a resident...'}</option>
            {residents.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} (Room {r.room_number})
              </option>
            ))}
          </select>
        </div>

        {/* Assessment Type */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
            Assessment Type <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <select name="type" value={formData.type} onChange={handleChange} style={inputStyle}>
            <option value="">Select type...</option>
            {assessmentTypes.map((type) => (
              <option key={type} value={type}>
                {typeLabels[type]}
              </option>
            ))}
          </select>
        </div>

        {/* Assessment Title */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
            Assessment Title <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="e.g., Quarterly Review Q1 2026"
            style={inputStyle}
          />
        </div>

        {/* Due Date */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
            Due Date <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <input type="date" name="due_date" value={formData.due_date} onChange={handleChange} style={inputStyle} />
        </div>

        {/* Notes */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>Notes</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Assessment findings, observations, etc..."
            rows="4"
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        <div style={buttonContainerStyle}>
          <Btn outline color={B.muted} onClick={onClose}>
            Cancel
          </Btn>
          <Btn color={B.teal} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : isEdit ? 'Update Assessment' : 'Save Assessment'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

export function ViewAssessmentModal({ assessment, onClose }) {
  const { B } = useTheme();
  if (!assessment) return null;

  const typeLabels = {
    intake: 'Intake Assessment',
    quarterly: 'Quarterly Review',
    annual: 'Annual Assessment',
    incident: 'Incident Assessment',
    discharge: 'Discharge Assessment'
  };

  const modalStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  };

  const modalContentStyle = {
    background: B.cardBg,
    borderRadius: 16,
    width: '100%',
    maxWidth: 500,
    padding: 28,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    '@media (maxWidth: 768px)': {
      width: '90%',
      padding: 16,
    },
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 800, color: B.navy, marginBottom: 20 }}>Assessment Details</div>
        <Field label="Resident" value={assessment.resident_name || assessment.resident} />
        <Field label="Type" value={typeLabels[assessment.type] || assessment.type} />
        <Field label="Title" value={assessment.title} />
        <Field label="Due Date" value={new Date(assessment.due_date).toLocaleDateString()} />
        <Field label="Status" value={assessment.status?.replace('_', ' ')} />
        {assessment.completed_at && <Field label="Completed" value={new Date(assessment.completed_at).toLocaleDateString()} />}
        {assessment.notes && <Field label="Notes" value={assessment.notes} />}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <Btn outline color={B.muted} onClick={onClose}>
            Close
          </Btn>
        </div>
      </div>
    </div>
  );
}

export function CompleteAssessmentModal({ assessment, onClose, onComplete }) {
  const { B } = useTheme();
  const [findings, setFindings] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [completedBy, setCompletedBy] = useState('');
  const [completionDate, setCompletionDate] = useState(new Date().toISOString().split('T')[0]);

  const handleComplete = async () => {
    if (!completedBy) {
      alert('Please select who completed the assessment');
      return;
    }

    setSubmitting(true);
    try {
      if (onComplete) {
        await onComplete({
          findings,
          completed_by: completedBy,
          completed_at: completionDate
        });
      }
      onClose();
    } catch (err) {
      console.error('Failed to complete assessment:', err);
      alert('Failed to complete assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const typeLabels = {
    intake: 'Intake Assessment',
    quarterly: 'Quarterly Review',
    annual: 'Annual Assessment',
    incident: 'Incident Assessment',
    discharge: 'Discharge Assessment'
  };

  const modalStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  };

  const modalContentStyle = {
    background: B.cardBg,
    borderRadius: 16,
    width: '100%',
    maxWidth: 480,
    padding: 28,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    '@media (maxWidth: 768px)': {
      width: '90%',
      padding: 16,
    },
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 8,
    border: `1.5px solid ${B.border}`,
    fontSize: 13,
    background: B.inputBg,
    color: B.text,
    outline: 'none',
    fontFamily: 'inherit',
  };

  const buttonContainerStyle = {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    '@media (maxWidth: 768px)': {
      flexDirection: 'column',
      gap: 8,
    },
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 800, color: B.navy, marginBottom: 20 }}>Complete Assessment</div>
        {assessment && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: B.navy }}>{assessment.resident_name || assessment.resident}</div>
            <div style={{ fontSize: 12, color: B.muted }}>{typeLabels[assessment.type] || assessment.type}</div>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
            Completion Date <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <input type="date" value={completionDate} onChange={(e) => setCompletionDate(e.target.value)} style={inputStyle} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
            Completed By <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <select value={completedBy} onChange={(e) => setCompletedBy(e.target.value)} style={inputStyle}>
            <option value="">Select staff...</option>
            <option value="Alex Franklin (RN)">Alex Franklin (RN)</option>
            <option value="Sandra Miller (LPN)">Sandra Miller (LPN)</option>
            <option value="Dr. Robert Chen">Dr. Robert Chen</option>
            <option value="Dr. Linda Park">Dr. Linda Park</option>
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>Findings & Recommendations</label>
          <textarea
            value={findings}
            onChange={(e) => setFindings(e.target.value)}
            placeholder="Document your findings, observations, and recommendations..."
            rows="4"
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        <div style={buttonContainerStyle}>
          <Btn outline color={B.muted} onClick={onClose}>
            Cancel
          </Btn>
          <Btn color={B.green} onClick={handleComplete} disabled={submitting}>
            {submitting ? 'Saving...' : 'Mark Complete'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

export default AssessmentModal;